
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  useDisclosure,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Radio,
  Stack,
  RadioGroup,
  Heading,
  Icon,
} from "@chakra-ui/react";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";
import { GoToPage } from "../../components/CustomPagination/GoToPage";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FiEye, FiGrid, FiList, FiX } from "react-icons/fi";
import { MdStorefront } from "react-icons/md";

import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import {  DetailModal } from "views/storeReport/modals/DetailModal";
import { AbstractView } from "views/storeReport/components/AbstractView";
import {AdjustedQtyModal } from "views/storeReport/modals/AdjustedQtyModal";
import { ActivationImageModal } from "views/storeReport/modals/ActivationImageModal";
import { CoolerImageModal } from "views/storeReport/modals/CoolerImageModal";
import { useImages } from "views/storeReport/hooks/useImages";
import { useRecalculation } from "views/storeReport/hooks/useRecalculation";
import { useAdjustedQty } from "views/storeReport/hooks/useAdjustedQty";

// ─── Helpers 
const parseComments = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    const products = parsed.filter((item) => item?.productName);
    const grouped = products.reduce((acc, item) => {
      const name = item?.productName?.trim();
      if (!name) return acc;
      const count = Number(item?.count ?? item?.productCount ?? 0);
      acc[name] = Math.max(acc[name] || 0, count);
      return acc;
    }, {});
    return Object.entries(grouped).map(([name, count]) => `${name} - ${count}`);
  } catch {
    return [];
  }
};


// ─── Cell Renderers 

const CommentsCellRenderer = ({ value }) => {
  const lines = parseComments(value);
  if (lines.length === 0)
    return (
      <span style={{ color: "#A0AEC0", fontSize: "13px", lineHeight: "60px" }}>
        —
      </span>
    );
  return (
    <div style={{ width: "100%", padding: "6px 0" }}>
      {lines.map((line, i) => (
        <div
          key={i}
          style={{
            fontSize: "13px",
            color: "#0a0b0e",
            lineHeight: "1.8",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflow: "visible",
            paddingBottom: "2px",
          }}
        >
          • {line}
        </div>
      ))}
    </div>
  );
};

const StoreNameCellRenderer = ({ value }) => {
  if (!value) return <span style={{ color: "#A0AEC0" }}>—</span>;
  return (
    <div
      title={value}
      style={{
        fontSize: "14px",
        color: "#0a0b0e",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
        cursor: "default",
      }}
    >
      {value}
    </div>
  );
};

const PAGE_SIZE = 10;


function AvailabilityDp({ fromDate, toDate }) {

  const [rowData, setRowData]             = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [currentPage, setCurrentPage]     = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const { isOpen, onOpen, onClose }       = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const toast  = useToast({ position: "top" });
  const [activeTab, setActiveTab] = useState(0); 
  const [selectedRow, setSelectedRow]         = useState(null);
  const [loadingDetails, setLoadingDetails]   = useState(false);

  const [storeDetails, setStoreDetails]           = useState(null);
  const [storeImages, setStoreImages]             = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageRotation, setImageRotation]         = useState(0);


  const [scoreBreakdown, setScoreBreakdown]             = useState(null);
  const [productSummary, setProductSummary]             = useState([]);
  const [shelfInfo, setShelfInfo]                       = useState([]);
  const [currentActualPaths, setCurrentActualPaths]       = useState([]);
  const [currentAnnotatedPaths, setCurrentAnnotatedPaths] = useState([]);
  const [currentStorePaths, setCurrentStorePaths]         = useState([]);
  const [isValidatingAvl, setIsValidatingAvl] = useState(false);
  const [isValidatingAct, setIsValidatingAct] = useState(false);
  const [storeFilter, setStoreFilter] = useState("TOTAL");
  const [allocatedCount, setAllocatedCount] = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);

  const images = useImages();

  const buildDateBody = useCallback(() => {
    const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
    const [toYear, toMonth, toDay]       = toDate.split("-").map(Number);
    return {
      from: { date: fromDay,  month: fromMonth, year: fromYear },
      to:   { date: toDay,    month: toMonth,   year: toYear   },
    };
  }, [fromDate, toDate]);


  

 
  const fetchCounts = useCallback(async () => {
    if (!fromDate || !toDate) return;
    try {
      const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
      const body = buildDateBody();
      const [allocatedRes, verifiedRes] = await Promise.all([
        apiClient.post(
          `${API_CONFIG.ENDPOINTS.VARIANCE_REPORT.AVAILABILITY}?page=0&size=1&operation=A&userId=${userId}`,
          body
        ),
        apiClient.post(
          `${API_CONFIG.ENDPOINTS.VARIANCE_REPORT.AVAILABILITY}?page=0&size=1&operation=V&userId=${userId}`,
          body
        ),
      ]);
      setAllocatedCount(allocatedRes.data?.totalElements ?? 0);
      setVerifiedCount(verifiedRes.data?.totalElements ?? 0);
    } catch (err) {
      console.error("Failed to fetch counts:", err);
    }
  }, [fromDate, toDate, buildDateBody]);

  const fetchData = useCallback(
    async (page = 0, filter = storeFilter) => {
      if (!fromDate || !toDate) return;
      setLoading(true);
      setError(null);
      setRowData([]);
      try {
        const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
        let queryParams = `page=${page}&size=${PAGE_SIZE}`;
        if (filter === "ALLOCATED") queryParams += `&operation=A&userId=${userId}`;
        if (filter === "VERIFIED")  queryParams += `&operation=V&userId=${userId}`;

        const res  = await apiClient.post(
          `${API_CONFIG.ENDPOINTS.VARIANCE_REPORT.AVAILABILITY}?${queryParams}`,
          buildDateBody()
        );
        const data       = res.data;
        const content    = Array.isArray(data) ? data : data?.content ?? [];
        const totalEl    = data?.totalElements ?? content.length;
        const totalPg    = data?.totalPages ?? 1;
        const pageNumber = data?.number ?? page;

        setRowData(
          content.map((item) => ({
            storeId:           item?.storeId           ?? "",
            storeName:         item?.storeName         ?? "",
            storeType:         item?.storeType         ?? "",
            availabilityScore: item?.availabilityScore ?? "",
            comments:          item?.comments          ?? "",
            _raw:              item,
          }))
        );
        setTotalElements(totalEl);
        setTotalPages(totalPg);
        setCurrentPage(pageNumber);
      } catch (err) {
        console.error("Failed to fetch availability data:", err);
        setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, buildDateBody]
  );

  useEffect(() => {
    fetchData(0, storeFilter);
    fetchCounts();
  }, [fetchData, fetchCounts]);

  const handleFilterChange = (val) => {
    setStoreFilter(val);
    fetchData(0, val);
  };

  // ── Open row detail 



  const handleEyeClick = async (rowNode) => {
    setActiveTab(0);
    setSelectedRow(rowNode);
    setStoreDetails(null);
    setStoreImages([]);
    setScoreBreakdown(null);
    setProductSummary([]);
    setShelfInfo([]);
    images.setActualImageUrl("");
    images.setAnnotatedImageUrl("");
    images.setModalActualImages([]);
    images.setModalAnnotatedImages([]);
    images.setModalImageIndex(0);
    setCurrentImageIndex(0);
    setImageRotation(0);
    onOpen();
    setLoadingDetails(true);

    try {
      const body= buildDateBody();
      const [, toMonth, toYear] = toDate.split("-").map(Number);

      const[detailsRes,breakdownRes]=await Promise.all([
        apiClient.post(
        `${API_CONFIG.ENDPOINTS.ASM_REPORT.ABSTRACTVIEW}?storeId=${rowNode.storeId}`,
        body
      ),
      apiClient.post(
          `/red-master/${rowNode.storeId}/score-breakdown`,
          body
        ),

      ])
  
      const details = detailsRes.data || null;
      const bd = breakdownRes.data;
        setStoreDetails(details);
        setScoreBreakdown(bd);
        setProductSummary(bd?.availability?.products || []);
        setLoadingDetails(false);

      const actualPaths    = bd?.availability?.imagePathResponse?.s3pathActualFile    || [];
      const annotatedPaths = bd?.availability?.imagePathResponse?.s3pathAnnotatedFile || [];
      const storePaths     = (details?.imagePaths || []).map(String);


      setCurrentActualPaths(actualPaths);      
      setCurrentAnnotatedPaths(annotatedPaths); 
      setCurrentStorePaths(storePaths); 
      setShelfInfo(actualPaths.map((_, i) => ({ shelfNumber: i + 1, label: `Shelf ${i + 1}` })));
       
        const [storeUrls, actualUrls, annotatedUrls] = await Promise.all([
      storePaths.length
        ? images.generatePresignedUrls(storePaths, toMonth, toYear)
        : Promise.resolve([]),
      actualPaths.length
        ? images.generatePresignedUrls(actualPaths, toMonth, toYear)
        : Promise.resolve([]),
      annotatedPaths.length
        ? images.generatePresignedUrls(annotatedPaths, toMonth, toYear)
        : Promise.resolve([]),
    ]);

    setStoreImages(storeUrls);
    images.setModalActualImages(actualUrls);
    images.setActualImageUrl(actualUrls[0] || "");
    images.setModalAnnotatedImages(annotatedUrls);
    images.setAnnotatedImageUrl(annotatedUrls[0] || "");

    actualUrls.slice(0, 3).forEach(src => { new window.Image().src = src; });
      annotatedUrls.slice(0, 3).forEach(src => { new window.Image().src = src; });
      
    } catch (err) {
      console.error("Failed to load store details:", err);
      setLoadingDetails(false);
    }
  };


  // ── Download image 

  const handleDownloadImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl, { mode: "cors", cache: "no-cache" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      const link = document.createElement("a");
      link.href     = imageUrl;
      link.download = "image.jpg";
      link.target   = "_blank";
      link.rel      = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleValidate = async (auditType, setLoading) => {
  const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
  const storeId = selectedRow?.storeId || scoreBreakdown?.availability?.products?.[0]?.storeId;
  const auditDate = storeDetails?.auditedDate;
  const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");

  if (!runId || !storeId || !auditDate || !userId) {
    toast({ title: "Missing Data", description: "Required information is missing.", status: "warning", duration: 3000, isClosable: true });
    return;
  }
  setLoading(true);
  try {
    await apiClient.post(`${API_CONFIG.BASE_URL}/api/audit/validate-store`, {
      runId: Number(runId), storeId: Number(storeId), auditType, auditDate, userId: Number(userId),
    });
    toast({ title: "Success", description: "Store validated successfully", status: "success", duration: 3000, isClosable: true });
  } catch (err) {
    toast({ title: "Validation failed", description: err.response?.data?.message || "Failed to validate store", status: "error", duration: 3000, isClosable: true });
  } finally {
    setLoading(false);
  }
};
const { handleAdjustedQty, adjustedQtyData, isLoadingAdjustedQty, adjustedQtyError, isAdjustedQtyOpen, onAdjustedQtyClose } = useAdjustedQty({
    getStoreId:     () => selectedRow?.storeId || scoreBreakdown?.store?.storeId,
    getAuditedDate: () => storeDetails?.auditedDate,
  });
  const refreshModalData = useCallback(async (showToast = false) => {
  if (!selectedRow) return;
  setLoadingDetails(true);
  try {
    const body = buildDateBody();
    const [, toMonth, toYear] = toDate.split("-").map(Number);

    const detailsRes = await apiClient.post(
      `${API_CONFIG.ASM_REPORT.ABSTRACTVIEW}?storeId=${selectedRow.storeId}`,
      body
    );
    const details = detailsRes.data || null;
    setStoreDetails(details);

    if (details?.imagePaths?.length > 0) {
      const paths = details.imagePaths.map((p) => `${p}`);
      setCurrentStorePaths(paths);
      const urls = await images.generatePresignedUrls(paths, toMonth, toYear); 
      setStoreImages(urls);
    }

    if (details?.storeId) {
      const breakdownRes = await apiClient.post(
        `/red-master/${details.storeId}/score-breakdown`,
        body
      );
      const bd = breakdownRes.data;
      setScoreBreakdown(bd);
      setProductSummary(bd?.availability?.products || []);

      if (showToast) {
        toast({
          title: "Data Refreshed",
          description: "Store data has been updated successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }

      const actualPaths    = bd?.availability?.imagePathResponse?.s3pathActualFile    || [];
      const annotatedPaths = bd?.availability?.imagePathResponse?.s3pathAnnotatedFile || [];

      setShelfInfo(actualPaths.map((_, i) => ({ shelfNumber: i + 1, label: `Shelf ${i + 1}` })));

      
      if (actualPaths.length) {
        setCurrentActualPaths(actualPaths);
        const urls = await images.generatePresignedUrls(actualPaths, toMonth, toYear); 
        setModalActualImages(urls);
        setActualImageUrl(urls[modalImageIndex] || urls[0] || "");
      }

      if (annotatedPaths.length) {
        setCurrentAnnotatedPaths(annotatedPaths);
        const urls = await images.generatePresignedUrls(annotatedPaths, toMonth, toYear); 
        setModalAnnotatedImages(urls);
        setAnnotatedImageUrl(urls[modalImageIndex] || urls[0] || "");
      }
    }
  } catch (err) {
    console.error("Failed to refresh modal data:", err);
  } finally {
    setLoadingDetails(false); 
  }
}, [selectedRow, buildDateBody, toDate, images.modalImageIndex, toast, images]);

 const { handleRecalculation, isRecalculating } = useRecalculation({
  getRunId:   () => scoreBreakdown?.store?.runId,
  getStoreId: () => scoreBreakdown?.store?.storeId,
  onSuccess: async () => {
    await refreshModalData(false);
    fetchData(currentPage);
  },
});

const handleRefresh = async () => {
  await refreshModalData(true);
  fetchData(currentPage);
};

const handleEditSave = async () => {
  try {
    await refreshModalData(false);  
    fetchData(currentPage);          
  } catch (err) {
    console.error("Failed to refresh after edit:", err);
  }
};
  

  

  // ── Column definitions ─────────────────────────────────────────────────────

  const EyeCellRenderer = ({ data }) => (
    <Flex align="center" justify="center" h="100%">
      <IconButton
        aria-label="View details"
        icon={<FiEye />}
        size="sm"
        colorScheme="red"
        variant="ghost"
        color="red.500"
        _hover={{ bg: "red.50", transform: "scale(1.15)" }}
        transition="all 0.2s"
        onClick={() => handleEyeClick(data)}
      />
    </Flex>
  );

  const columnDefs = [
    {
      headerName: "VIEW", field: "_action",
      minWidth: 80, maxWidth: 90, flex: 0,
      sortable: false, filter: false, suppressMovable: true,
      cellRenderer: EyeCellRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    { headerName: "STORE ID",   field: "storeId",   minWidth: 120, flex: 1 },
    {
      headerName: "STORE NAME", field: "storeName", minWidth: 180, flex: 1.5,
      cellRenderer: StoreNameCellRenderer,
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
    },
    { headerName: "STORE TYPE", field: "storeType", minWidth: 130, flex: 1 },
    {
      headerName: "AVAILABILITY SCORE", field: "availabilityScore", minWidth: 160, flex: 1,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", color: "#2D3748" },
    },
    {
      headerName: "COMMENTS", field: "comments", minWidth: 280, flex: 2.5,
      cellRenderer: CommentsCellRenderer,
      autoHeight: false, wrapText: false,
      cellStyle: { display: "flex", alignItems: "flex-start", paddingTop: "6px", paddingBottom: "6px", overflow: "hidden" },
    },
  ];

  const handlePrevious = () => { if (currentPage > 0) fetchData(currentPage - 1); };
  const handleNext     = () => { if (currentPage < totalPages - 1) fetchData(currentPage + 1); };
  const handleGoToPage = (page) => { // page is 1-based
    fetchData(Math.min(Math.max(page - 1, 0), totalPages - 1));
  };

  const radioStyle = {
  ".chakra-radio__control": {
    borderColor: "black",
    borderWidth: "2px",
  },
  ".chakra-radio__label": {
    color: "black",
    fontWeight: "bold",
  },
};


  return (
    <Box>
      {/* Record count pill */}
      <Flex align="center"  justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <RadioGroup value={storeFilter} onChange={handleFilterChange}>
          <Stack direction="row" spacing={6} align="center">
            <Radio value="TOTAL" colorScheme="red"  fontSize="lg" color="black" sx={radioStyle} >
              <Text color="gray.900" fontWeight="bold" fontSize="md">
               Total Stores
               </Text>
          </Radio>
            <Radio value="ALLOCATED" colorScheme="red" fontWeight="semibold" fontSize="md" sx={radioStyle}>
               <Text color="gray.900" fontWeight="bold" fontSize="md">
              Allocated Stores
              </Text>
            </Radio>
            <Radio value="VERIFIED" colorScheme="red" fontWeight="semibold" fontSize="md" sx={radioStyle}>
               <Text color="gray.900" fontWeight="bold" fontSize="md">
              Verified Stores
              </Text>
            </Radio>
          </Stack>
        </RadioGroup>
        <Flex
          align="center" gap={1.5} bg="gray.700"
          px={4} py={1.5} borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.2)"
        >
          <Text fontSize="sm" fontWeight="bold" color="white">Total:</Text>
          <Text color="white" fontSize="md" fontWeight="bold">{totalElements} Records</Text>
        </Flex>
      </Flex>

      {error && (
        <Text color="red.500" fontWeight="bold" mb={3}>{error}</Text>
      )}

      {/* AG Grid */}
      <Box mb={3}>
        <AgGridWrapper
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
          height="600px"
          extraGridProps={{
            suppressPaginationPanel: true,
            headerHeight: 65,
            getRowHeight: (params) => {
              const lines = parseComments(params.data?.comments);
              const lineCount = Math.max(lines.length, 1);
              return lineCount * 28 + 16;
            },
            animateRows: true,
          }}
          noRowsMessage="No records found for the selected date range"
        />
      </Box>

      {/* Pagination */}
      <Flex justify="space-between" align="center" mt={4} px={2}>
        <Text fontSize="sm" color="gray.600">
          {totalElements > 0
            ? `Showing ${currentPage * PAGE_SIZE + 1} to ${Math.min(
                currentPage * PAGE_SIZE + rowData.length,
                totalElements
              )} of ${totalElements} records`
            : "No records"}
        </Text>
        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious} isDisabled={currentPage === 0 || loading}
            size="sm" variant="outline" colorScheme="gray" leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>
          <Flex
            align="center" justify="center" bg="red.500" color="white"
            w="40px" h="40px" borderRadius="md" fontWeight="bold" fontSize="md"
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {currentPage + 1}
          </Flex>
          <Text fontSize="sm" color="gray.600">
            of {totalPages}
          </Text>
          <GoToPage totalPages={totalPages} onGo={handleGoToPage} isDisabled={loading} />
          <Button
            onClick={handleNext} isDisabled={currentPage >= totalPages - 1 || loading}
            size="sm" variant="outline" colorScheme="gray" rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      <Modal
        isOpen={isOpen}
        onClose={()=>{onClose();
          setActiveTab(0);
        }}
        size="6xl"
        isCentered
        motionPreset="slideInBottom"
        scrollBehavior="inside"
        blockScrollOnMount={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" bg="white" borderRadius="2xl" boxShadow="2xl" overflow="hidden">

          {/* Header */}
          <ModalHeader
            bgGradient="linear(to-r, red.600, red.500)"
            color="white" py={6} px={8}
            borderBottom="1px solid" borderColor="red.700"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={3}>
                <Icon as={MdStorefront} boxSize={7} />
                <Box>
                  <Heading size="md" fontWeight="bold">Store Detail View</Heading>
                  <Text fontSize="sm" color="red.100" mt={1}>
                    {storeDetails?.storeName || "—"} · Availability
                  </Text>
                </Box>
              </Flex>
              <IconButton
                aria-label="Close"
                icon={<FiX />}
                variant="ghost"
                color="white"
                _hover={{ bg: "red.700" }}
                onClick={()=>{onClose()
                  setActiveTab(0)
                }}
                size="lg"
              />
            </Flex>
          </ModalHeader>

          <ModalBody px={8} py={6} overflowY="scroll">

            {/* ── Toggle Buttons ─────────────────────────────────────────── */}
            <Flex bg="red.100" p={2} borderRadius="lg" gap={2} display="inline-flex" mb={6}>
              <Button
                leftIcon={<FiGrid size={18} />}
                fontWeight="semibold" fontSize="md" py={3} px={6} borderRadius="lg"
                onClick={() => setActiveTab(0)}
                bg={activeTab === 0 ? "red.600" : "transparent"}
                color={activeTab === 0 ? "white" : "gray.600"}
                boxShadow={activeTab === 0 ? "md" : "none"}
                _hover={{
                  bg: activeTab === 0 ? "red.600" : "red.200",
                  color: activeTab === 0 ? "white" : "red.700",
                }}
                transition="all 0.2s"
              >
                Store View
              </Button>
              <Button
          leftIcon={<FiList size={18} />}
          fontWeight="semibold" fontSize="md" py={3} px={6} borderRadius="lg"
          onClick={() => {
            setActiveTab(1);
            onDetailOpen();    
          }}
          bg={activeTab === 1 ? "red.600" : "transparent"}
          color={activeTab === 1 ? "white" : "gray.600"}
          boxShadow={activeTab === 1 ? "md" : "none"}
          _hover={{
            bg: activeTab === 1 ? "red.600" : "red.200",
            color: activeTab === 1 ? "white" : "red.700",
          }}
          transition="all 0.2s"
        >
          Detail View
         </Button>
            </Flex>

            {/* ── Abstract View ───────────────────────────────────────────── */}
            {activeTab === 0 && (
              <AbstractView
                loadingStoreDetails={loadingDetails}
                selectedStoreDetails={storeDetails}
                selectedStoreImages={storeImages}
                currentImageIndex={currentImageIndex}
                setCurrentImageIndex={setCurrentImageIndex}
                imageRotation={imageRotation}
                setImageRotation={setImageRotation}
                onDownload={handleDownloadImage}
              />
            )}

          </ModalBody>

        </ModalContent>
      </Modal>
      <DetailModal
        isOpen={isDetailOpen}
        onClose={() => { onDetailClose(); onClose(); }}
        isOrgUser={false}
        storeData={{ scoreBreakdown, productSummary, selectedStoreDetails: storeDetails, shelfInfo }}
        images={{
          actualImageUrl:       images.actualImageUrl,
          annotatedImageUrl:    images.annotatedImageUrl,
          modalActualImages:    images.modalActualImages,
          modalAnnotatedImages: images.modalAnnotatedImages,
          modalImageIndex:      images.modalImageIndex,
          onPrevImage:          images.handlePrevModalImage,
          onNextImage:          images.handleNextModalImage,
          loadingCoolerImage:   images.loadingCoolerImage,
          generatePresignedUrls: images.generatePresignedUrls,
          onViewActivation:     (detail) => images.handleViewActivation(detail, toDate),
          onViewCoolerImage:    (fileName) => images.handleViewCoolerImage(fileName, toDate),
        }}
        actions={{
          onRecalculate:  handleRecalculation,
          isRecalculating,
          onValidateAvl:  () => handleValidate("AVL", setIsValidatingAvl),
          isValidatingAvl,
          onValidateAct:  () => handleValidate("ACT", setIsValidatingAct),
          isValidatingAct,
          onAdjustedQty:  handleAdjustedQty,
          onRefresh:      handleRefresh,
        }}
        editModal={{ isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose, onSave: handleEditSave }}
      />

      <AdjustedQtyModal
      isOpen={isAdjustedQtyOpen}
      onClose={onAdjustedQtyClose}
      isLoading={isLoadingAdjustedQty}
      error={adjustedQtyError}
      data={adjustedQtyData}
    />
    <ActivationImageModal
      isOpen={images.isActivationImageOpen}
      onClose={() => images.setIsActivationImageOpen(false)}
      imageUrl={images.activationImageUrl}
      isLoading={images.loadingActivationImage}
      onDownload={images.handleDownloadImage}
    />
    <CoolerImageModal
      isOpen={images.isCoolerImageOpen}
      onClose={() => images.setIsCoolerImageOpen(false)}
      imageUrl={images.coolerImageUrl}
      isLoading={images.loadingCoolerImage}
      onDownload={images.handleDownloadImage}
    />

    </Box>
  );
}

export default AvailabilityDp;